// @ts-nocheck
import { alpha, createTheme } from "@mui/material/styles";
import { ptBR } from "@mui/material/locale";

const colors = {
  primary: {
    main: "#1f4d4d",
    light: "#3c6d6b",
    dark: "#163535",
    contrastText: "#fdf7ef",
  },
  secondary: {
    main: "#c86b4a",
    light: "#e3926f",
    dark: "#a35335",
    contrastText: "#1b1612",
  },
  success: {
    main: "#2f8462",
    light: "#5dab8c",
    dark: "#1f5c44",
  },
  warning: {
    main: "#d08a3d",
    light: "#e9b067",
    dark: "#b06f2a",
  },
  error: {
    main: "#c44d45",
    light: "#df7a72",
    dark: "#9e3832",
  },
  info: {
    main: "#2c6fa3",
    light: "#5f9ac7",
    dark: "#1d4f76",
  },
  background: {
    default: "#f6efe6",
    paper: "#fdf7ef",
  },
  text: {
    primary: "#1b1a17",
    secondary: "#5c5248",
  },
};

const typography = {
  fontFamily: ["\"Hanken Grotesk\"", "sans-serif"].join(","),
  h1: {
    fontSize: "2.375rem",
    fontWeight: 700,
    lineHeight: 1.15,
    letterSpacing: "-0.02em",
    fontFamily: "\"Fraunces\", serif",
  },
  h2: {
    fontSize: "1.875rem",
    fontWeight: 700,
    lineHeight: 1.2,
    letterSpacing: "-0.015em",
    fontFamily: "\"Fraunces\", serif",
  },
  h3: {
    fontSize: "1.5rem",
    fontWeight: 600,
    lineHeight: 1.3,
    fontFamily: "\"Fraunces\", serif",
  },
  h4: {
    fontSize: "1.25rem",
    fontWeight: 600,
    lineHeight: 1.35,
    fontFamily: "\"Fraunces\", serif",
  },
  h5: {
    fontSize: "1.05rem",
    fontWeight: 600,
    lineHeight: 1.4,
    fontFamily: "\"Fraunces\", serif",
  },
  h6: {
    fontSize: "0.95rem",
    fontWeight: 600,
    lineHeight: 1.4,
    fontFamily: "\"Fraunces\", serif",
  },
  subtitle1: { fontSize: "1rem", fontWeight: 500, lineHeight: 1.7 },
  subtitle2: { fontSize: "0.875rem", fontWeight: 600, lineHeight: 1.6 },
  body1: { fontSize: "1rem", fontWeight: 400, lineHeight: 1.6 },
  body2: { fontSize: "0.9rem", fontWeight: 400, lineHeight: 1.55 },
  button: {
    fontSize: "0.9rem",
    fontWeight: 600,
    lineHeight: 1.6,
    textTransform: "none",
    letterSpacing: "0.01em",
  },
  caption: { fontSize: "0.75rem", fontWeight: 500, lineHeight: 1.6 },
  overline: {
    fontSize: "0.65rem",
    fontWeight: 600,
    lineHeight: 2.4,
    textTransform: "uppercase",
    letterSpacing: "0.12em",
  },
};

const spacing = 8;

const breakpoints = {
  values: { xs: 0, sm: 600, md: 960, lg: 1280, xl: 1920 },
};

const components = {
  MuiCssBaseline: {
    styleOverrides: {
      ":root": {
        "--ag-festas-bg": colors.background.default,
        "--ag-festas-surface": colors.background.paper,
        "--ag-festas-ink": colors.text.primary,
        "--ag-festas-muted": colors.text.secondary,
        "--ag-festas-accent": colors.primary.main,
        "--ag-festas-accent-2": colors.secondary.main,
        "--ag-festas-ring": alpha(colors.primary.main, 0.25),
      },
      "*, *::before, *::after": {
        boxSizing: "border-box",
      },
      body: {
        margin: 0,
        fontFamily: typography.fontFamily,
        minHeight: "100vh",
        color: colors.text.primary,
        backgroundColor: colors.background.default,
        backgroundImage: `radial-gradient(circle at 15% 15%, ${alpha(
          colors.secondary.light,
          0.18,
        )} 0%, transparent 55%), radial-gradient(circle at 85% -10%, ${alpha(
          colors.primary.light,
          0.18,
        )} 0%, transparent 55%), linear-gradient(180deg, ${alpha(
          "#ffffff",
          0.7,
        )} 0%, transparent 60%)`,
        backgroundAttachment: "fixed",
      },
      "#root": {
        minHeight: "100vh",
      },
      "img, svg": {
        display: "block",
        maxWidth: "100%",
      },
      a: {
        color: colors.primary.main,
        textDecoration: "none",
      },
      "a:hover": {
        color: colors.secondary.main,
      },
      "::selection": {
        backgroundColor: alpha(colors.secondary.main, 0.3),
      },
      "@media (prefers-reduced-motion: reduce)": {
        "*, *::before, *::after": {
          animationDuration: "0.01ms !important",
          animationIterationCount: "1 !important",
          transitionDuration: "0.01ms !important",
          scrollBehavior: "auto !important",
        },
      },
      "@keyframes pageFadeUp": {
        "0%": { opacity: 0, transform: "translateY(12px)" },
        "100%": { opacity: 1, transform: "translateY(0)" },
      },
    },
  },
  MuiButton: {
    styleOverrides: {
      root: {
        borderRadius: 12,
        textTransform: "none",
        fontWeight: 600,
        padding: "10px 18px",
        boxShadow: "none",
      },
      containedPrimary: {
        background: `linear-gradient(135deg, ${colors.primary.main}, ${colors.primary.dark})`,
        boxShadow: `0 10px 18px ${alpha(colors.primary.dark, 0.25)}`,
        "&:hover": {
          background: `linear-gradient(135deg, ${colors.primary.dark}, ${colors.primary.main})`,
          boxShadow: `0 12px 24px ${alpha(colors.primary.dark, 0.3)}`,
        },
      },
      containedSecondary: {
        background: `linear-gradient(135deg, ${colors.secondary.main}, ${colors.secondary.dark})`,
        boxShadow: `0 10px 18px ${alpha(colors.secondary.dark, 0.25)}`,
        "&:hover": {
          background: `linear-gradient(135deg, ${colors.secondary.dark}, ${colors.secondary.main})`,
          boxShadow: `0 12px 24px ${alpha(colors.secondary.dark, 0.3)}`,
        },
      },
    },
  },
  MuiCard: {
    styleOverrides: {
      root: {
        borderRadius: 18,
        border: `1px solid ${alpha(colors.primary.main, 0.08)}`,
        backgroundImage: `linear-gradient(180deg, ${alpha(
          "#ffffff",
          0.8,
        )}, ${alpha(colors.background.paper, 0.95)})`,
        boxShadow: "0 16px 40px rgba(26, 22, 18, 0.08)",
        transition: "transform 0.2s ease, box-shadow 0.2s ease",
        "&:hover": {
          transform: "translateY(-2px)",
          boxShadow: "0 20px 48px rgba(26, 22, 18, 0.12)",
        },
      },
    },
  },
  MuiPaper: {
    styleOverrides: {
      root: {
        borderRadius: 14,
        border: `1px solid ${alpha(colors.primary.main, 0.08)}`,
        backgroundColor: colors.background.paper,
      },
      elevation1: {
        boxShadow: "0 8px 20px rgba(26, 22, 18, 0.08)",
      },
    },
  },
  MuiTextField: {
    styleOverrides: {
      root: {
        "& .MuiOutlinedInput-root": {
          borderRadius: 12,
          backgroundColor: colors.background.paper,
          transition: "box-shadow 0.2s ease, border-color 0.2s ease",
          "&:hover .MuiOutlinedInput-notchedOutline": {
            borderColor: alpha(colors.primary.main, 0.35),
          },
          "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
            borderColor: colors.primary.main,
            borderWidth: 2,
          },
          "&.Mui-focused": {
            boxShadow: `0 0 0 3px ${alpha(colors.primary.main, 0.18)}`,
          },
        },
      },
    },
  },
  MuiChip: {
    styleOverrides: {
      root: {
        borderRadius: 999,
        fontWeight: 600,
        backgroundColor: alpha(colors.primary.main, 0.08),
      },
    },
  },
  MuiTableHead: {
    styleOverrides: {
      root: {
        backgroundColor: alpha(colors.primary.main, 0.08),
        "& .MuiTableCell-head": {
          fontWeight: 700,
          fontSize: "0.85rem",
          color: colors.text.primary,
          textTransform: "uppercase",
          letterSpacing: "0.08em",
        },
      },
    },
  },
  MuiDataGrid: {
    styleOverrides: {
      root: {
        borderRadius: 14,
        border: `1px solid ${alpha(colors.primary.main, 0.12)}`,
        "& .MuiDataGrid-columnHeaders": {
          backgroundColor: alpha(colors.primary.main, 0.08),
          borderBottom: `1px solid ${alpha(colors.primary.main, 0.16)}`,
        },
        "& .MuiDataGrid-cell": {
          borderBottom: `1px solid ${alpha(colors.primary.main, 0.08)}`,
        },
        "& .MuiDataGrid-row:hover": {
          backgroundColor: alpha(colors.primary.main, 0.06),
        },
      },
    },
  },
  MuiDrawer: {
    styleOverrides: {
      paper: {
        backgroundColor: colors.background.paper,
        borderRight: `1px solid ${alpha(colors.primary.main, 0.12)}`,
        backgroundImage: `linear-gradient(180deg, ${alpha(
          colors.primary.light,
          0.08,
        )} 0%, transparent 40%)`,
      },
    },
  },
  MuiAppBar: {
    styleOverrides: {
      root: {
        background: `linear-gradient(120deg, ${colors.primary.dark} 0%, ${colors.primary.main} 55%, ${colors.primary.light} 120%)`,
        boxShadow: "0 10px 24px rgba(19, 18, 16, 0.18)",
        borderBottom: `1px solid ${alpha(colors.primary.light, 0.25)}`,
        backdropFilter: "blur(8px)",
      },
    },
  },
  MuiDialog: {
    styleOverrides: {
      paper: {
        borderRadius: 20,
        border: `1px solid ${alpha(colors.primary.main, 0.12)}`,
      },
    },
  },
  MuiSnackbar: {
    styleOverrides: {
      root: {
        "& .MuiSnackbarContent-root": {
          borderRadius: 12,
        },
      },
    },
  },
};

const shadows = [
  "none",
  "0px 1px 2px rgba(19, 16, 12, 0.06)",
  "0px 2px 6px rgba(19, 16, 12, 0.08)",
  "0px 4px 10px rgba(19, 16, 12, 0.1)",
  "0px 6px 16px rgba(19, 16, 12, 0.12)",
  "0px 8px 20px rgba(19, 16, 12, 0.14)",
  "0px 10px 24px rgba(19, 16, 12, 0.16)",
  "0px 12px 28px rgba(19, 16, 12, 0.18)",
  "0px 14px 32px rgba(19, 16, 12, 0.2)",
  "0px 16px 36px rgba(19, 16, 12, 0.22)",
  "0px 18px 40px rgba(19, 16, 12, 0.24)",
  "0px 20px 44px rgba(19, 16, 12, 0.26)",
  "0px 22px 48px rgba(19, 16, 12, 0.28)",
  "0px 24px 52px rgba(19, 16, 12, 0.3)",
  "0px 26px 56px rgba(19, 16, 12, 0.32)",
  "0px 28px 60px rgba(19, 16, 12, 0.34)",
  "0px 30px 64px rgba(19, 16, 12, 0.36)",
  "0px 32px 68px rgba(19, 16, 12, 0.38)",
  "0px 34px 72px rgba(19, 16, 12, 0.4)",
  "0px 36px 76px rgba(19, 16, 12, 0.42)",
  "0px 38px 80px rgba(19, 16, 12, 0.44)",
  "0px 40px 84px rgba(19, 16, 12, 0.46)",
  "0px 42px 88px rgba(19, 16, 12, 0.48)",
  "0px 44px 92px rgba(19, 16, 12, 0.5)",
  "0px 46px 96px rgba(19, 16, 12, 0.52)",
];

export const theme = createTheme(
  {
    palette: colors,
    typography,
    spacing,
    breakpoints,
    components,
    shadows,
    shape: { borderRadius: 12 },
    zIndex: { drawer: 1200, appBar: 1100, modal: 1300 },
  },
  ptBR,
);

export const darkTheme = createTheme(
  {
    ...theme,
    palette: {
      mode: "dark",
      primary: {
        main: "#4aa3a1",
        light: "#79c4c1",
        dark: "#2c6f6b",
        contrastText: "#0b0d0d",
      },
      secondary: {
        main: "#e39a73",
        light: "#f1b99a",
        dark: "#c36a4a",
        contrastText: "#1a120d",
      },
      success: {
        main: "#5cbf96",
        light: "#86d4b4",
        dark: "#3a8d6a",
      },
      warning: {
        main: "#e7a65a",
        light: "#f3c082",
        dark: "#c9823d",
      },
      error: {
        main: "#d56c62",
        light: "#e6938a",
        dark: "#b04a42",
      },
      info: {
        main: "#6aa6d7",
        light: "#90bfe3",
        dark: "#3f79a8",
      },
      background: { default: "#0f1211", paper: "#171c1a" },
      text: { primary: "#f2ece2", secondary: "#c1b7aa" },
    },
    components: {
      ...components,
      // O gradiente do tema claro vai do verde-petróleo ao quase preto, feito para
      // texto claro. Aqui o texto é escuro (`contrastText`), então o gradiente
      // sobe: do tom claro ao principal. Todos os pontos passam de 4.5:1 (AA).
      MuiButton: {
        styleOverrides: {
          root: {
            borderRadius: 12,
            textTransform: "none",
            fontWeight: 600,
            padding: "10px 18px",
            boxShadow: "none",
          },
          containedPrimary: {
            background: "linear-gradient(135deg, #79c4c1, #4aa3a1)",
            boxShadow: `0 10px 18px ${alpha("#000000", 0.35)}`,
            "&:hover": {
              background: "linear-gradient(135deg, #4aa3a1, #79c4c1)",
              boxShadow: `0 12px 24px ${alpha("#000000", 0.4)}`,
            },
          },
          containedSecondary: {
            background: "linear-gradient(135deg, #f1b99a, #e39a73)",
            boxShadow: `0 10px 18px ${alpha("#000000", 0.35)}`,
            "&:hover": {
              background: "linear-gradient(135deg, #e39a73, #f1b99a)",
              boxShadow: `0 12px 24px ${alpha("#000000", 0.4)}`,
            },
          },
        },
      },
      MuiCssBaseline: {
        styleOverrides: {
          ":root": {
            "--ag-festas-bg": "#0f1211",
            "--ag-festas-surface": "#171c1a",
            "--ag-festas-ink": "#f2ece2",
            "--ag-festas-muted": "#c1b7aa",
            "--ag-festas-accent": "#4aa3a1",
            "--ag-festas-accent-2": "#e39a73",
            "--ag-festas-ring": alpha("#4aa3a1", 0.3),
            colorScheme: "dark",
          },
          "*, *::before, *::after": {
            boxSizing: "border-box",
          },
          body: {
            margin: 0,
            fontFamily: typography.fontFamily,
            minHeight: "100vh",
            color: "#f2ece2",
            backgroundColor: "#0f1211",
            backgroundImage: `radial-gradient(circle at 15% 15%, ${alpha(
              "#4aa3a1",
              0.16,
            )} 0%, transparent 55%), radial-gradient(circle at 85% -10%, ${alpha(
              "#e39a73",
              0.14,
            )} 0%, transparent 55%), linear-gradient(180deg, ${alpha(
              "#000000",
              0.5,
            )} 0%, transparent 60%)`,
            backgroundAttachment: "fixed",
          },
          "#root": {
            minHeight: "100vh",
          },
          "img, svg": {
            display: "block",
            maxWidth: "100%",
          },
          a: {
            color: "#79c4c1",
            textDecoration: "none",
          },
          "a:hover": {
            color: "#f1b99a",
          },
          "::selection": {
            backgroundColor: alpha("#e39a73", 0.3),
          },
          "@media (prefers-reduced-motion: reduce)": {
            "*, *::before, *::after": {
              animationDuration: "0.01ms !important",
              animationIterationCount: "1 !important",
              transitionDuration: "0.01ms !important",
              scrollBehavior: "auto !important",
            },
          },
          "@keyframes pageFadeUp": {
            "0%": { opacity: 0, transform: "translateY(12px)" },
            "100%": { opacity: 1, transform: "translateY(0)" },
          },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: {
            border: `1px solid ${alpha("#ffffff", 0.08)}`,
            backgroundImage: "none",
            backgroundColor: "#171c1a",
            boxShadow: "0 18px 40px rgba(0, 0, 0, 0.45)",
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            border: `1px solid ${alpha("#ffffff", 0.08)}`,
            backgroundColor: "#171c1a",
          },
          elevation1: {
            boxShadow: "0 12px 28px rgba(0, 0, 0, 0.4)",
          },
        },
      },
      MuiDrawer: {
        styleOverrides: {
          paper: {
            backgroundColor: "#121614",
            borderRight: `1px solid ${alpha("#ffffff", 0.08)}`,
            backgroundImage: `linear-gradient(180deg, ${alpha(
              "#4aa3a1",
              0.12,
            )} 0%, transparent 45%)`,
          },
        },
      },
      MuiAppBar: {
        styleOverrides: {
          root: {
            background: "linear-gradient(120deg, #183b39 0%, #25514d 55%, #2d6f6b 120%)",
            borderBottom: `1px solid ${alpha("#ffffff", 0.12)}`,
            // Sombra própria (sem ela a AppBar escura usa a de elevação do Paper):
            // o cabeçalho do menu lateral copia esta, e as duas faixas se encontram.
            boxShadow: `0 10px 24px ${alpha("#000000", 0.35)}`,
          },
        },
      },
      MuiTableHead: {
        styleOverrides: {
          root: {
            backgroundColor: alpha("#ffffff", 0.06),
            "& .MuiTableCell-head": {
              color: "#f2ece2",
            },
          },
        },
      },
      MuiDataGrid: {
        styleOverrides: {
          root: {
            border: `1px solid ${alpha("#ffffff", 0.12)}`,
            "& .MuiDataGrid-columnHeaders": {
              backgroundColor: alpha("#ffffff", 0.06),
              borderBottom: `1px solid ${alpha("#ffffff", 0.12)}`,
            },
            "& .MuiDataGrid-cell": {
              borderBottom: `1px solid ${alpha("#ffffff", 0.08)}`,
            },
            "& .MuiDataGrid-row:hover": {
              backgroundColor: alpha("#ffffff", 0.04),
            },
          },
        },
      },
      MuiTextField: {
        styleOverrides: {
          root: {
            "& .MuiOutlinedInput-root": {
              backgroundColor: alpha("#ffffff", 0.04),
            },
          },
        },
      },
      MuiChip: {
        styleOverrides: {
          root: {
            backgroundColor: alpha("#4aa3a1", 0.2),
          },
        },
      },
    },
  },
  ptBR,
);

// Cores de status do modo ativo: `mode` é `theme.palette.mode`. As do tema claro
// ficam apagadas sobre o fundo escuro, então o escuro usa as suas próprias.
export const getStatusColor = (status, mode: "light" | "dark" = "light") => {
  const paleta = mode === "dark" ? darkTheme.palette : colors;
  switch (status) {
    case "A":
    case "ativo":
    case "aberto":
      return paleta.info.main;
    case "F":
    case "finalizado":
    case "concluido":
      return paleta.success.main;
    case "C":
    case "cancelado":
      return paleta.error.main;
    case "P":
    case "pendente":
      return paleta.warning.main;
    default:
      return paleta.text.secondary;
  }
};

export const getStatusText = (status) => {
  switch (status) {
    case "A":
      return "Aberto";
    case "F":
      return "Finalizado";
    case "C":
      return "Cancelado";
    case "P":
      return "Pendente";
    case 1:
    case "1":
      return "Ativo";
    case 0:
    case "0":
      return "Inativo";
    default:
      return status || "N/A";
  }
};

export const getTipoClienteText = (tipo) => {
  switch (tipo) {
    case 1:
    case "1":
      return "Pessoa Física";
    case 2:
    case "2":
      return "Pessoa Jurídica";
    default:
      return "Não informado";
  }
};

export default theme;
